import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Custom hooks
import { useAuth } from "hooks/useAuth";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Material Dashboard 2 React routes
import routes from "routes";

const MainLayout = ({ children }) => {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();

  // Check if current route is an authentication page
  const isAuthPage = pathname.startsWith("/authentication");
  
  // Only show dashboard layout if user is logged in and not on auth pages
  const shouldShowDashboard = isLoggedIn && !isAuthPage;

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const configsButton = shouldShowDashboard ? (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  ) : null;

  // Render dashboard layout
  const renderDashboardLayout = () => (
    <>
      <Sidenav
        color={sidenavColor}
        brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
        brandName={direction === "rtl" ? "منصة البلد" : "Material Dashboard 2"}
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      />
      <Configurator />
      {configsButton}
    </>
  );

  // Render authentication layout (no sidebar)
  const renderAuthLayout = () => (
    <>
      {/* Authentication pages don't need sidebar or configurator */}
    </>
  );

  // Get the appropriate theme based on direction and dark mode
  const getTheme = () => {
    if (direction === "rtl") {
      return darkMode ? themeDarkRTL : themeRTL;
    }
    return darkMode ? themeDark : theme;
  };

  // Render with RTL support
  if (direction === "rtl") {
    return (
      <>
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={getTheme()}>
            <CssBaseline />
            {shouldShowDashboard && renderDashboardLayout()}
            {layout === "vr" && <Configurator />}
            {children}
          </ThemeProvider>
        </CacheProvider>
      </>
    );
  }

  // Render with LTR support
  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      {shouldShowDashboard && renderDashboardLayout()}
      {layout === "vr" && <Configurator />}
      {children}
    </ThemeProvider>
  );
};

export default MainLayout;
